import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Chip, Autocomplete,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { additionalProcessService, productService } from '../services/api';

const EMPTY_FORM = { icon: '⚙️', name: '', description: '', is_active: true, product_ids: [] };

export default function AdditionalProcessesSettingsPage() {
  const [processes, setProcesses] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    additionalProcessService.getAll().then(r => setProcesses(r.data.results || r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    productService.getAll({ page_size: 500, ordering: 'name' })
      .then(r => setProducts(r.data.results || r.data || []));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      icon: p.icon || '⚙️',
      name: p.name,
      description: p.description || '',
      is_active: p.is_active,
      product_ids: (p.products_info || []).map(pr => pr.id),
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Назва обовʼязкова'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await additionalProcessService.update(editingId, form);
      } else {
        await additionalProcessService.create(form);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(e?.response?.data?.name?.[0] || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити процес?')) return;
    await additionalProcessService.remove(id);
    load();
  };

  const handleToggleActive = async (p) => {
    await additionalProcessService.update(p.id, { is_active: !p.is_active });
    load();
  };

  const selectedProducts = products.filter(p => form.product_ids.includes(p.id));

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="#1565c0">
          Додаткові виробничі процеси
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: '#1565c0' }} onClick={openAdd}>
          Додати процес
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
              <TableCell width={60}>Іконка</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Вироби</TableCell>
              <TableCell width={120} align="center">Активний</TableCell>
              <TableCell width={100} align="center">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  Процеси не додані
                </TableCell>
              </TableRow>
            )}
            {processes.map(p => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontSize: 24, textAlign: 'center' }}>{p.icon}</TableCell>
                <TableCell>
                  <Typography fontWeight="medium">{p.name}</Typography>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{p.description || '—'}</TableCell>
                <TableCell>
                  {(p.products_info || []).length === 0 ? (
                    <Chip label="Для всіх виробів" size="small" variant="outlined" color="default" />
                  ) : (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {p.products_info.map(pr => (
                        <Chip
                          key={pr.id}
                          label={pr.article ? `${pr.article} — ${pr.name}` : pr.name}
                          size="small"
                          sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                        />
                      ))}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Switch checked={p.is_active} onChange={() => handleToggleActive(p)} color="primary" />
                  {p.is_active
                    ? <Chip label="Так" size="small" color="success" />
                    : <Chip label="Ні" size="small" color="default" />
                  }
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(p)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(p.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1565c0' }}>
          {editingId ? 'Редагувати процес' : 'Додати процес'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Іконка (emoji)"
            value={form.icon}
            onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
            inputProps={{ maxLength: 4 }}
            helperText="Введіть один або два символи emoji"
          />
          <TextField
            label="Назва"
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            error={!!error && !form.name.trim()}
          />
          <TextField
            label="Опис"
            multiline
            rows={2}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
          <Autocomplete
            multiple
            options={products}
            value={selectedProducts}
            onChange={(_, newVal) => setForm(f => ({ ...f, product_ids: newVal.map(p => p.id) }))}
            getOptionLabel={p => p.article ? `${p.article} — ${p.name}` : p.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField
                {...params}
                label="Прив'язані вироби"
                helperText="Залиште порожнім — процес буде відображатися для всіх виробів"
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={option.id}
                  label={option.article ? `${option.article} — ${option.name}` : option.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
          />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
            label="Активний"
          />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#1565c0' }}>
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
